/**
 * ListActionsBar — Roomgrid filter bar.
 *
 * Filters apply LIVE while typing (debounced ~350ms, backend filtering is
 * case-insensitive contains) — there is no Search button.
 *
 * Layout (desktop):
 *   [primary filters] [«Filters» button + count badge] [active chips] [Clear] [extra] [add]
 * The «Filters» button (shown when `advancedFilters` is provided) toggles a
 * collapsible auto-fill grid panel — the Roomgrid advanced-filter pattern.
 *
 * Mobile: primary + advanced filters live in a bottom drawer; values apply
 * live there too.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { useListContext, useTranslate } from "react-admin";
import { FILTER_HEIGHT } from "./FilterField";
import {
  FilterBarContext,
  type FilterBarContextValue,
} from "./filterBarContext";

export interface ListActionsBarProps {
  filters: ReactNode;
  /** Extra filters shown inside the collapsible «Filters» panel. */
  advancedFilters?: ReactNode;
  addButton?: ReactNode;
  extraActions?: ReactNode;
  /**
   * Title shown inline with the filter/add icons on mobile only.
   * On desktop the bar does not render any title — the caller keeps its
   * own page-title element above the list.
   */
  title?: string;
}

const APPLY_DEBOUNCE_MS = 350;

const isEmptyValue = (v: unknown): boolean => {
  if (v === undefined || v === null || v === "") return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
};

const normalize = (
  values: Record<string, unknown> | undefined,
): Record<string, unknown> => {
  if (!values) return {};
  const out: Record<string, unknown> = {};
  Object.entries(values).forEach(([k, v]) => {
    if (!isEmptyValue(v)) out[k] = v;
  });
  return out;
};

const shallowEqual = (
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean => {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a[k] === b[k]);
};

const countActive = (values: Record<string, unknown>): number =>
  Object.values(values).filter((v) => !isEmptyValue(v)).length;

const ListActionsBar: React.FC<ListActionsBarProps> = ({
  filters,
  advancedFilters,
  addButton,
  extraActions,
  title,
}) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const translate = useTranslate();
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const appliedValues = useMemo(
    () => normalize(filterValues as Record<string, unknown> | undefined),
    [filterValues],
  );

  const [pendingValues, setPendingValues] =
    useState<Record<string, unknown>>(appliedValues);

  // Labels registered by FilterFields — used to name the applied chips.
  const labelsRef = useRef<Record<string, string>>({});
  const registerLabel = useCallback((source: string, label: string) => {
    labelsRef.current[source] = label;
  }, []);

  const setPendingValue = useCallback((source: string, value: unknown) => {
    setPendingValues((prev) => {
      const next = { ...prev };
      if (isEmptyValue(value)) delete next[source];
      else next[source] = value;
      return next;
    });
  }, []);

  // Live apply: debounce pending → setFilters. The pending state is the
  // source of truth while the user types; we never overwrite it from the
  // server round-trip (that would eat keystrokes mid-flight).
  const setFiltersRef = useRef(setFilters);
  setFiltersRef.current = setFilters;
  const appliedRef = useRef(appliedValues);
  appliedRef.current = appliedValues;

  useEffect(() => {
    if (shallowEqual(pendingValues, appliedRef.current)) return;
    const timer = setTimeout(() => {
      setFiltersRef.current(pendingValues, displayedFilters);
    }, APPLY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingValues]);

  const ctxValue = useMemo<FilterBarContextValue>(
    () => ({ pendingValues, setPendingValue, registerLabel }),
    [pendingValues, setPendingValue, registerLabel],
  );

  const activeCount = countActive(pendingValues);
  const hasAny = activeCount > 0 || countActive(appliedValues) > 0;

  const removeFilter = (source: string): void => {
    setPendingValue(source, "");
  };

  const clearAll = (): void => {
    setPendingValues({});
    setFilters({}, displayedFilters);
  };

  const activeChips = (
    <>
      {Object.entries(pendingValues).map(([source, value]) => (
        <Chip
          key={source}
          size="small"
          label={`${labelsRef.current[source] ?? source}: ${String(value)}`}
          onDelete={() => removeFilter(source)}
          sx={{
            backgroundColor: rg.accentWeak,
            color: rg.accent,
            fontWeight: 600,
            "& .MuiChip-deleteIcon": {
              color: rg.accent,
              fontSize: 14,
              "&:hover": { color: rg.accent, opacity: 0.7 },
            },
          }}
        />
      ))}
    </>
  );

  const clearButton = hasAny ? (
    <Button variant="text" onClick={clearAll} sx={{ flexShrink: 0 }}>
      {translate("common.clear", { _: "Clear" })}
    </Button>
  ) : null;

  const filtersToggle = advancedFilters ? (
    <Button
      variant="outlined"
      onClick={() => setPanelOpen((v) => !v)}
      startIcon={<FilterIcon sx={{ fontSize: 15 }} />}
      sx={{
        flexShrink: 0,
        height: FILTER_HEIGHT,
        ...(panelOpen && { borderColor: rg.accent, color: rg.accent }),
      }}
    >
      {translate("ra.action.filter", { _: "Filters" })}
      {activeCount > 0 && (
        <Box
          component="span"
          className="rg-mono"
          sx={{
            ml: "7px",
            minWidth: 16,
            height: 16,
            px: "4px",
            borderRadius: "99px",
            backgroundColor: rg.accentWeak,
            color: rg.accent,
            fontSize: "10.5px",
            fontWeight: 600,
            display: "grid",
            placeItems: "center",
          }}
        >
          {activeCount}
        </Box>
      )}
    </Button>
  ) : null;

  if (!isMobile) {
    return (
      <FilterBarContext.Provider value={ctxValue}>
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              // The Roomgrid theme renders field labels ABOVE the input, so
              // each filter is label+input tall while buttons are input-only
              // tall — bottom-align everything to the input row.
              alignItems: "flex-end",
              gap: 1.25,
              width: "100%",
              flexWrap: "wrap",
              p: 0,
              m: 0,
            }}
          >
            {filters}
            {filtersToggle}
            <Box
              sx={{
                display: "flex",
                gap: "6px",
                flexWrap: "wrap",
                alignItems: "center",
                alignSelf: "flex-end",
                minHeight: FILTER_HEIGHT,
              }}
            >
              {activeChips}
            </Box>
            {clearButton}
            {extraActions}
            {addButton && <Box sx={{ ml: "auto" }}>{addButton}</Box>}
          </Box>
          {advancedFilters && (
            <Collapse in={panelOpen} timeout={160} unmountOnExit={false}>
              <Box
                sx={{
                  mt: 1.5,
                  p: "14px",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: "12px 14px",
                  border: `1px solid ${rg.border}`,
                  borderRadius: "14px",
                  backgroundColor: rg.bgElev,
                }}
              >
                {advancedFilters}
              </Box>
            </Collapse>
          )}
        </Box>
      </FilterBarContext.Provider>
    );
  }

  return (
    <FilterBarContext.Provider value={ctxValue}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          width: "100%",
          p: 0,
          m: 0,
        }}
      >
        {title ? (
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
        <Badge
          color="secondary"
          variant="dot"
          invisible={activeCount === 0}
          overlap="circular"
        >
          <IconButton
            onClick={() => setDrawerOpen(true)}
            aria-label={translate("ra.action.filter", { _: "Filters" })}
            sx={{ height: FILTER_HEIGHT, width: FILTER_HEIGHT }}
          >
            <FilterIcon />
          </IconButton>
        </Badge>
        {extraActions}
        {addButton}
      </Box>
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: "85vh",
          },
        }}
      >
        <Box sx={{ p: 2, pb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {translate("ra.action.filter", { _: "Filters" })}
            </Typography>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              aria-label={translate("ra.action.close", { _: "Close" })}
              size="small"
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Stack spacing={2}>
            {filters}
            {advancedFilters}
          </Stack>
          {hasAny && (
            <Button
              fullWidth
              variant="text"
              onClick={clearAll}
              sx={{ mt: 2 }}
            >
              {translate("common.clear", { _: "Clear" })}
            </Button>
          )}
        </Box>
      </Drawer>
    </FilterBarContext.Provider>
  );
};

export default ListActionsBar;
